import { createApi } from "@reduxjs/toolkit/query/react";
import * as config from "config";
import * as utils from "utils";

const baseQuery = async (args, api, extraOptions) => {
  try {
    const response = await utils.authenticatedFetch(
      config.endpoints.bff(args.url),
      {
        headers: { "Content-Type": "application/json" },
        method: "POST",
        ...args.payload,
      }
    );
    return {
      data: await response.json(),
    };
  } catch (e) {
    return {
      error: String(e),
    };
  }
};

export const bffApi = createApi({
  reducerPath: "bffApi",
  baseQuery: baseQuery,
  endpoints: (builder) => ({
    fetchUrlMetadata: builder.query({
      query: ({ url }) => ({
        url: "fetch-url-metadata",
        payload: {
          body: JSON.stringify({ url }),
        },
      }),
    }),
    createInsuredSummary: builder.query({
      query: ({ submissionId }) => ({
        url: "create-insured-summary",
        payload: {
          body: JSON.stringify({ submissionId }),
        },
      }),
    }),
    listForms: builder.query({
      query: () => ({
        url: "list-forms",
      }),
    }),
    createForm: builder.mutation({
      query: ({ formData }) => ({
        url: "create-form",
        payload: {
          body: JSON.stringify({ formData }),
        },
      }),
    }),
    retrieveForm: builder.query({
      query: ({ formId }) => ({
        url: "retrieve-form",
        payload: {
          body: JSON.stringify({ formId }),
        },
      }),
    }),
    updateForm: builder.mutation({
      query: ({ formId, formData }) => ({
        url: "update-form",
        payload: {
          body: JSON.stringify({ formId, formData }),
        },
      }),
    }),
    submitForm: builder.mutation({
      query: ({ formId }) => ({
        url: "submit-form",
        payload: {
          body: JSON.stringify({ formId }),
        },
      }),
    }),
    resubmitForm: builder.mutation({
      query: ({ formId }) => ({
        url: "resubmit-form",
        payload: {
          body: JSON.stringify({ formId }),
        },
      }),
    }),
    actionQuotedForm: builder.mutation({
      query: ({ formId, action, layerId }) => ({
        url: "action-quoted-form",
        payload: {
          body:
            layerId == null
              ? JSON.stringify({ formId, action })
              : JSON.stringify({ formId, action, layerId }),
        },
      }),
    }),
    addInception: builder.mutation({
      query: ({ formId, inception, layerId }) => ({
        url: "add-inception",
        payload: {
          body:
            layerId == null
              ? JSON.stringify({ formId, inception })
              : JSON.stringify({ formId, inception, layerId }),
        },
      }),
    }),
    createProgramSubmission: builder.mutation({
      query: ({ formData, program = "CAUSE" }) => ({
        url: "create-program-submission",
        payload: {
          body: JSON.stringify({ program, formData }),
        },
      }),
    }),
    updateProgramSubmission: builder.mutation({
      query: ({
        formData,
        updateFilename,
        priorSubmissionFilename,
        program = "CAUSE",
      }) => ({
        url: "update-program-submission",
        payload: {
          body:
            priorSubmissionFilename == null
              ? JSON.stringify({ program, formData, updateFilename })
              : JSON.stringify({
                  program,
                  formData,
                  updateFilename,
                  priorSubmissionFilename,
                }),
        },
      }),
    }),
  }),
});

export const {
  useFetchUrlMetadataQuery,
  useCreateInsuredSummaryQuery,
  useListFormsQuery,
  useCreateFormMutation,
  useRetrieveFormQuery,
  useUpdateFormMutation,
  useSubmitFormMutation,
  useResubmitFormMutation,
  useActionQuotedFormMutation,
  useAddInceptionMutation,
  useCreateProgramSubmissionMutation,
  useUpdateProgramSubmissionMutation,
} = bffApi;
